import { appendIf } from '@btime/helpers'
import { assoc, compose, filter, is, map, pick, prop } from 'ramda'
import { gql, useMutation } from '@apollo/client'
import { useNotification } from '@btime/react-messager'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { placesClient } from '../../../../utils/apollo/api'
import { useCloseModal } from '../../../../components/modals/ModalsContext'
import { cleanFieldValuesRequest, validateFieldValues } from '../../../../utils/helpers/fields'
import { usePlace } from '../../../../modules/places/show/_load/PlaceLoader'

const MUTATION = gql`
  mutation UpsertAssets($inputs: [AssetInput]) {
    upsertAssets(inputs: $inputs) {
      id
    }
  }
`

const cleanRequest = map(
  compose(
    pick(['id', 'name', 'placeId', 'typeId', 'fieldValues', 'enabled']),
    item => assoc('placeId', item?.place?.id || null, item),
    item => assoc('typeId', item?.type?.id || null, item),
    item => assoc('fieldValues', cleanFieldValuesRequest(item.fieldValues || []), item),
    item => assoc('enabled', item.enabled !== false ? true : false, item)
  )
)

const validate = (form, setErrors, t) => {
  const errors = form.reduce(
    (errors, item, index) => [
      ...errors,
      ...appendIf(!item.name, { path: [index, 'name'], message: t('validations:required') }),
      ...validateFieldValues(form, [index, 'fieldValues'])
    ],
    []
  )

  setErrors(errors)
  return errors.length === 0
}

export const useSaveAssets = (disabled) => {
  const { t } = useTranslation(['validations'])
  const closeModal = useCloseModal()
  const place = usePlace()
  const placeId = place?.id
  const notify = useNotification()

  const [originalAssetIds, setOriginalAssetIds] = React.useState([])

  const refetchQueries = ['Assets', 'AssetsCount']
  if (disabled && placeId) {
    refetchQueries.push({
      query: gql`
        query AssetsForPlace($placeId: Int) {
          assets(filter: { place_ids: [$placeId] }) {
            id
          }
        }
      `,
      variables: { placeId }
    })
  }

  const [request, state] = useMutation(MUTATION, {
    refetchQueries,
    client: placesClient
  })

  React.useEffect(() => {
    if (place?.assets?.length > 0) {
      const ids = place.assets.map(asset => asset.id).filter(id => !!id)
      setOriginalAssetIds(ids)
    }
  }, [place])

  const handleRequest = (form, actions) => {
    const currentIds = form.map(item => item.id).filter(id => !!id)
    const deletedIds = originalAssetIds.filter(id => !currentIds.includes(id))

    const deletedAssets = deletedIds.map(id => ({ id, enabled: false }))
    const allInputs = [...form, ...deletedAssets]

    const inputs = cleanRequest(allInputs)
    const { setErrors } = actions
    const isValid = validate(form, setErrors, t)

    if (isValid) {
      return request({ variables: { inputs } }).then(resp => {
        if (!resp.errors) {
          notify.success({
            title: 'Ativos salvos com sucesso!',
            message: disabled ? 'Os ativos do local foram atualizados.' : 'Os ativos foram salvos com sucesso.'
          })

          if (!disabled) {
            closeModal()
          }
        }
      })
    }
  }

  return [handleRequest, state]
}
